// frontend/app/components/admin/LessonLibraryEditor.js
//
// One lesson, open for editing. The document, the save and the unlock live in
// useLessonDocument; this file is what an admin sees of them.
//
// Every outcome is stated on the page rather than in an alert that can be
// dismissed before it is read: what was rejected before sending, what the
// server refused, what was saved, and what the lock now means.

import React, { useState } from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import LessonMetadataFields from "./LessonMetadataFields";
import LessonStepFields from "./LessonStepFields";
import LessonUnlockDialog from "./LessonUnlockDialog";
import useLessonDocument from "./useLessonDocument";
import { addBlock, moveBlock, patchBlock, patchStep, removeBlock } from "./lessonAdminDraft";

const BackLink = ({ colors, onPress }) => (
  <TouchableOpacity onPress={onPress} accessibilityRole="button" style={styles.back}>
    <MaterialCommunityIcons name="chevron-left" size={18} color={colors.accentPrimary} />
    <Text style={[styles.backText, { color: colors.accentPrimary }]}>
      Back to the library
    </Text>
  </TouchableOpacity>
);

const LessonLibraryEditor = ({ colors, lessonPlanId, onClose, onSaved }) => {
  const {
    draft,
    loading,
    loadError,
    dirty,
    catalog,
    change,
    save,
    saving,
    saveError,
    problems,
    savedNote,
    unlock,
    unlocking,
    unlockError,
    dismissUnlockError,
  } = useLessonDocument(lessonPlanId, { onSaved });

  const [openStep, setOpenStep] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  const edit = (next) => {
    setConfirmingLeave(false);
    change(next);
  };

  // Leaving is only worth a question when there is something to lose, so an
  // untouched lesson closes on the first press.
  const leave = () => {
    if (dirty && !confirmingLeave) {
      setConfirmingLeave(true);
      return;
    }
    onClose();
  };

  const confirmUnlock = async () => {
    if (await unlock()) setUnlockOpen(false);
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="small" color={colors.accentPrimary} />
        <Text style={[styles.muted, { color: colors.textMuted }]}>Loading lesson…</Text>
      </View>
    );
  }

  if (loadError || !draft) {
    return (
      <View>
        <BackLink colors={colors} onPress={onClose} />
        <View
          style={[
            styles.notice,
            { backgroundColor: colors.incorrectBg, borderColor: colors.incorrectBorder },
          ]}
        >
          <Text style={[styles.noticeText, { color: colors.danger }]} selectable>
            {loadError || "That lesson could not be loaded."}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View>
      <View style={styles.headerRow}>
        <BackLink colors={colors} onPress={leave} />

        <View style={{ flex: 1 }} />

        {draft.content_locked ? (
          <TouchableOpacity
            onPress={() => {
              dismissUnlockError();
              setUnlockOpen(true);
            }}
            accessibilityRole="button"
            style={[styles.secondary, { borderColor: colors.borderStrong }]}
          >
            <Text style={[styles.secondaryText, { color: colors.textSecondary }]}>
              Unlock for reseed
            </Text>
          </TouchableOpacity>
        ) : null}

        <TouchableOpacity
          onPress={save}
          disabled={saving || !dirty}
          accessibilityRole="button"
          accessibilityState={{ disabled: saving || !dirty }}
          style={[
            styles.primary,
            { backgroundColor: colors.accentPrimary, opacity: saving || !dirty ? 0.45 : 1 },
          ]}
        >
          <Text style={styles.primaryText}>
            {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
          </Text>
        </TouchableOpacity>
      </View>

      {confirmingLeave ? (
        <View
          style={[
            styles.notice,
            { backgroundColor: colors.teacherTipBg, borderColor: colors.teacherTipBorder },
          ]}
        >
          <Text style={[styles.noticeText, { color: colors.teacherTipText }]}>
            This lesson has unsaved changes. Leaving now discards them.
          </Text>
          <View style={styles.noticeActions}>
            <TouchableOpacity
              onPress={() => setConfirmingLeave(false)}
              accessibilityRole="button"
            >
              <Text style={[styles.noticeAction, { color: colors.textSecondary }]}>
                Stay here
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={onClose} accessibilityRole="button">
              <Text style={[styles.noticeAction, { color: colors.danger }]}>
                Discard and go back
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

      {draft.content_locked ? (
        <View
          style={[
            styles.lockBar,
            { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder },
          ]}
        >
          <MaterialCommunityIcons name="lock" size={15} color={colors.info} />
          <Text style={[styles.lockText, { color: colors.textSecondary }]}>
            Locked{draft.content_locked_by ? ` by ${draft.content_locked_by}` : ""} — an
            ordinary seed skips this lesson. A forced seed overwrites it and clears the
            lock.
          </Text>
        </View>
      ) : (
        <View
          style={[
            styles.lockBar,
            { backgroundColor: colors.subtleBg, borderColor: colors.border },
          ]}
        >
          <MaterialCommunityIcons name="file-sync-outline" size={15} color={colors.textMuted} />
          <Text style={[styles.lockText, { color: colors.textSecondary }]}>
            File-managed. Saving here locks the lesson so the next seed cannot overwrite the
            edit.
          </Text>
        </View>
      )}

      {problems.length ? (
        <View
          style={[
            styles.notice,
            { backgroundColor: colors.teacherTipBg, borderColor: colors.teacherTipBorder },
          ]}
        >
          <Text style={[styles.noticeTitle, { color: colors.teacherTipText }]}>
            Not sent — fix these first:
          </Text>
          {problems.map((problem) => (
            <Text key={problem} style={[styles.noticeText, { color: colors.teacherTipText }]}>
              • {problem}
            </Text>
          ))}
        </View>
      ) : null}

      {saveError ? (
        <View
          style={[
            styles.notice,
            { backgroundColor: colors.incorrectBg, borderColor: colors.incorrectBorder },
          ]}
        >
          <Text style={[styles.noticeTitle, { color: colors.danger }]}>Not saved</Text>
          <Text style={[styles.noticeText, { color: colors.danger }]} selectable>
            {saveError}
          </Text>
        </View>
      ) : null}

      {savedNote ? (
        <View
          style={[
            styles.notice,
            { backgroundColor: colors.correctBg, borderColor: colors.correctBorder },
          ]}
        >
          <Text style={[styles.noticeText, { color: colors.success }]}>{savedNote}</Text>
        </View>
      ) : null}

      <LessonMetadataFields colors={colors} draft={draft} onChange={edit} />

      <Text style={[styles.stepsHeading, { color: colors.textSecondary }]}>
        Steps ({draft.steps.length})
      </Text>

      {/* Said out loud rather than left as a missing button. Content inside a
          step is fully editable; the steps themselves are not, and an admin
          hunting for an "Add step" control deserves to know that is deliberate
          rather than unfinished. */}
      <Text style={[styles.stepsNote, { color: colors.textMuted }]}>
        Steps themselves are fixed here — a lesson's phases run in a set order and each
        step key is what every student answer is filed against. Add, edit and reorder the
        content inside a step; change the steps in the lesson file and reseed.
      </Text>

      {draft.steps.map((step, index) => (
        <LessonStepFields
          key={step.step_key || `step-${index}`}
          colors={colors}
          step={step}
          index={index}
          expanded={openStep === index}
          onToggle={() => setOpenStep(openStep === index ? null : index)}
          onChange={(fields) => edit(patchStep(draft, index, fields))}
          onBlockAdd={(type) => edit(addBlock(draft, index, type))}
          onBlockChange={(blockIndex, next) => edit(patchBlock(draft, index, blockIndex, next))}
          onBlockRemove={(blockIndex) => edit(removeBlock(draft, index, blockIndex))}
          onBlockMove={(blockIndex, delta) => edit(moveBlock(draft, index, blockIndex, delta))}
          concepts={draft.concepts}
          boardTopicId={draft.board_topic_id}
          catalog={catalog}
        />
      ))}

      <LessonUnlockDialog
        colors={colors}
        visible={unlockOpen}
        lessonTitle={draft.title}
        busy={unlocking}
        error={unlockError}
        onCancel={() => setUnlockOpen(false)}
        onConfirm={confirmUnlock}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  centered: { alignItems: "center", paddingVertical: 24, gap: 8 },
  muted: { fontSize: 12 },

  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 10,
  },
  back: { flexDirection: "row", alignItems: "center", gap: 2 },
  backText: { fontSize: 12, fontWeight: "700" },

  primary: { borderRadius: 9, paddingHorizontal: 14, paddingVertical: 9 },
  primaryText: { color: "#fff", fontSize: 12, fontWeight: "800" },
  secondary: { borderRadius: 9, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 9 },
  secondaryText: { fontSize: 12, fontWeight: "700" },

  lockBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
  },
  lockText: { fontSize: 12, lineHeight: 18, flex: 1 },

  notice: { borderRadius: 10, borderWidth: 1, padding: 12, marginBottom: 12, gap: 3 },
  noticeTitle: { fontSize: 12, fontWeight: "800", marginBottom: 2 },
  noticeText: { fontSize: 12, lineHeight: 18 },
  noticeActions: { flexDirection: "row", gap: 16, marginTop: 8 },
  noticeAction: { fontSize: 12, fontWeight: "700" },

  stepsHeading: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  stepsNote: { fontSize: 11, lineHeight: 17, marginBottom: 10 },
});

export default LessonLibraryEditor;
